import React, {useEffect, useState} from 'react';
import {
    Button,
    Col,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Spin,
    Select,
    Table,
    Tag,
    Card,
    Checkbox,
    List,
    Avatar,
    Typography,
    message
} from "antd";
import api from "../../api";
import './style.css'
import CropTiming from "../../Components/CropTiming";
import {
    PlusOutlined,
    EditOutlined
} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import {monthOptions} from "../../constants";
import {useTranslations} from "../../lang";
import CdnImage from "../../Components/CdnImage";
import CropInfo from "../../Components/CropInfo";
import {getMeasurementAddon} from "../../utils/measurements";

export function getProductMeasurementSystem(product) {
    return product?.measurement_system || 'metric';
}

export function initializeProductEditForm(form, product) {
    form.resetFields();
    form.setFieldsValue({
        ...product,
        measurement_system: getProductMeasurementSystem(product),
    });
}

export function buildCropFromProduct(product) {
    return {
        name: product.name,
        variety: product.variety || '',
        type: product.type || '',
        image: product.image || '',
        product_id: product.id,
        sow_min: product.sow_min || null,
        sow_max: product.sow_max || null,
        plant_min: product.plant_min || null,
        plant_max: product.plant_max || null,
        harvest_min: product.harvest_min || null,
        harvest_max: product.harvest_max || null,
        row_distance: product.row_distance ?? null,
        plant_distance: product.plant_distance ?? null,
        height: product.height ?? null,
        sowing_depth: product.sowing_depth ?? null,
        measurement_system: getProductMeasurementSystem(product),
        note: product.description || '',
        is_seedling: product.is_seedling || false,
        is_direct_sown: product.is_direct_sown || false,
        url: product.url || '',
        supplier: product.suppliers || product.offers?.[0]?.supplier?.name || '',
        in_stock: true,
        is_fully_configured: true
    };
}

export default function Products() {

    const {translate} = useTranslations()
    const navigate = useNavigate()

    const [crops, setCrops] = useState([])
    const [loading, setLoading] = useState(true)
    const [suppliers, setSuppliers] = useState([])
    const [drawerVisible, setDrawerVisible] = useState(false)
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [productDetails, setProductDetails] = useState(null)
    const [productDetailsLoading, setProductDetailsLoading] = useState(false)
    const [addingToMyCrops, setAddingToMyCrops] = useState(false)
    const [editModalVisible, setEditModalVisible] = useState(false)
    const [editLoading, setEditLoading] = useState(false)
    const [cropTypes, setCropTypes] = useState([])
    const [icons, setIcons] = useState([])

    const [filterCropForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const productMeasurementSystem = getProductMeasurementSystem(productDetails);
    const productDistanceUnit = getMeasurementAddon(productMeasurementSystem, 'distance');

    useEffect(() => {
        loadCrops()
        loadSuppliers()
        if (api.isAdmin()) {
            api.cropTypes().then(setCropTypes)
            api.getAllIcons().then(setIcons)
        }
    }, []);

    function loadCrops() {
        setLoading(true)
        const filters = filterCropForm.getFieldsValue()

        api.products(filters).then((cropsData) => {
            setCrops(cropsData)
            setLoading(false)
        }).catch(() => {
            setLoading(false)
        })
    }

    function loadSuppliers() {
        api.getProductSuppliers().then(setSuppliers)
    }

    function openProductDrawer(product) {
        setSelectedProduct(product)
        setDrawerVisible(true)
        setProductDetailsLoading(true)
        setProductDetails(null)
        
        api.getProduct(product.id).then((details) => {
            setProductDetails(details)
            setProductDetailsLoading(false)
        }).catch(() => {
            setProductDetailsLoading(false)
        })
    }

    function closeProductDrawer() {
        setDrawerVisible(false)
        setSelectedProduct(null)
        setProductDetails(null)
    }

    function addToMyCrops() {
        if (!productDetails) return;

        setAddingToMyCrops(true);

        const cropData = buildCropFromProduct(productDetails);

        api.createCrop(cropData)
            .then(() => {
                message.success(translate('Gewas toegevoegd aan mijn gewassen!'));
                navigate('/crops');
            })
            .catch((error) => {
                if (error.response?.data?.error === "CropLimitExceededException") {
                    Modal.error({
                        title: translate('Je hebt het maximum aantal gewassen bereikt'),
                        okText: translate('Ga naar account instellingen'),
                        onOk: () => navigate('/account'),
                        content: (
                            <div>
                                <p>{translate('Je hebt het maximum aantal gewassen bereikt voor je account.')}</p>
                                <p>{translate('Wil je meer gewassen toevoegen? Upgrade dan.')}</p>
                            </div>
                        )
                    });
                    return;
                }
                console.error('Error adding crop:', error);
                message.error(translate('Fout bij het toevoegen van het gewas.'));
            })
            .finally(() => {
                setAddingToMyCrops(false);
            });
    }

    function openEditModal() {
        if (!productDetails) return;
        initializeProductEditForm(editForm, productDetails);
        setEditModalVisible(true);
    }

    function handleEditSave() {
        editForm.validateFields().then(values => {
            setEditLoading(true);
            api.updateProduct(productDetails.id, values).then((updated) => {
                message.success(translate('Product bijgewerkt'));
                setProductDetails(updated);
                setEditModalVisible(false);
                loadCrops();
            }).finally(() => {
                setEditLoading(false);
            });
        });
    }

    const supplierOptions = suppliers.map(supplier => ({
        label: supplier.name,
        value: supplier.id
    }))

    return (
        <>
            <Row gutter={[12, 12]} className={"mar-b-1"}>
                <Col md={12} xs={12}>
                    <h1>{translate("Gewassen Database")}</h1>
                </Col>
                <Col md={12} xs={12} style={{textAlign: 'right'}}>
                    <Button onClick={() => navigate('/crops')}>
                        {translate('Mijn Gewassen')}
                    </Button>
                </Col>
            </Row>

            <Form form={filterCropForm} layout={"vertical"}>
                <Row gutter={[12, 12]}>
                    <Col md={4} xs={12}>
                        <Form.Item name={"search"} label={translate("Naam en/of ras")}>
                            <Input onPressEnter={loadCrops} placeholder={translate("Zoeken op naam en/of ras")}/>
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item name={"sow_in"} label={translate("Zaaiperiode")}>
                            <Select mode={"multiple"}
                                    placeholder={translate("Filter op zaaiperiode")}
                                    defaultValue={[]}
                                    options={monthOptions}
                                    allowClear={true}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item name={"plant_in"} label={translate("Plantperiode")}>
                            <Select mode={"multiple"}
                                    placeholder={translate("Filter op (ver)plantperiode")}
                                    defaultValue={[]}
                                    options={monthOptions}
                                    allowClear={true}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item name={"harvest_in"} label={translate("Oogstperiode")}>
                            <Select mode={"multiple"}
                                    placeholder={translate("Filter op oogstperiode")}
                                    defaultValue={[]}
                                    options={monthOptions}
                                    allowClear={true}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item name={"supplier_id"} label={translate("Leverancier")}>
                            <Select
                                placeholder={translate("Filter op leverancier")}
                                options={supplierOptions}
                                allowClear={true}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item label={translate("Sorteer")} name={"sort"}>
                            <Select
                                defaultValue={"popularity"} options={[
                                {
                                    label: translate("Meest gebruikt"),
                                    value: "popularity"
                                },
                                {
                                    label: translate("Nieuwste eerst"),
                                    value: "id_desc"
                                },
                                {
                                    label: translate("Oudste eerst"),
                                    value: "id_asc"
                                },
                                {
                                    label: translate("Naam A-Z"),
                                    value: "name_asc"
                                },
                                {
                                    label: translate("Naam Z-A"),
                                    value: "name_desc"
                                },
                                {
                                    label: translate("Zaaiperiode"),
                                    value: "sow_min_asc"
                                },
                                {
                                    label: translate("Plantperiode"),
                                    value: "plant_min_asc"
                                },
                                {
                                    label: translate("Oogstperiode"),
                                    value: "harvest_min_asc"
                                }
                            ]}></Select>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={2}>
                        <Form.Item label=" ">
                            <Button type={"primary"} onClick={loadCrops}>
                                {translate("Zoeken")}
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <Spin spinning={loading}>
                <Table
                    dataSource={crops}
                    rowKey="id"
                    onRow={(crop) => ({
                        onClick: () => openProductDrawer(crop),
                        style: { cursor: 'pointer' }
                    })}
                    columns={[
                        {
                            title: translate('Afbeelding'),
                            key: 'image',
                            width: '80px',
                            render(crop) {
                                return <CdnImage src={crop.image} width={60} height={60} alt={crop.name}/>
                            }
                        },
                        {
                            title: translate('Gewas'),
                            key: 'name',
                            render(crop) {
                                return (
                                    <div>
                                        <strong>
                                            <a href={`/products/${crop.id}`} onClick={(event) => event.stopPropagation()}>
                                                {crop.name}
                                            </a>
                                        </strong>
                                        {crop.variety && <div style={{color: '#666'}}>{crop.variety}</div>}
                                        {crop.type && <Tag color="blue">{crop.type}</Tag>}
                                        <div className="mobile-only" style={{marginTop: 4}}>
                                            {crop.suppliers && <div style={{color: '#999', fontSize: 12}}>{crop.suppliers}</div>}
                                            <CropTiming crop={crop} />
                                        </div>
                                    </div>
                                )
                            }
                        },
                        {
                            title: translate('Verkrijgbaar bij'),
                            key: 'suppliers',
                            responsive: ['md'],
                            render(crop) {
                                return crop.suppliers || '-'
                            }
                        },
                        {
                            title: translate('Timing'),
                            key: 'timing',
                            responsive: ['md'],
                            render(crop) {
                                return <CropTiming crop={crop} />
                            }
                        }
                    ]}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} van ${total} gewassen`
                    }}
                />
            </Spin>

            <Drawer
                title={selectedProduct ? `${selectedProduct.name} ${selectedProduct.variety ? `- ${selectedProduct.variety}` : ''}` : translate('Product Details')}
                placement="right"
                size="large"
                onClose={closeProductDrawer}
                open={drawerVisible}
            >
                {productDetailsLoading ? (
                    <Spin spinning={true} style={{ textAlign: 'center', padding: '40px' }}>
                        <div>{translate('Laden...')}</div>
                    </Spin>
                ) : productDetails ? (
                    <div>
                        <Card title={translate('Product Informatie')} style={{ marginBottom: '20px' }}>
                            <CropInfo crop={productDetails} disableEdit></CropInfo>
                            <Row gutter={[16, 16]}>
                                {productDetails.description && (
                                    <Col span={24}>
                                        <strong>{translate('Beschrijving')}:</strong>
                                        <div style={{ marginTop: '8px' }}>{productDetails.description}</div>
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={[16, 16]}>
                                {productDetails.sowing_description && (
                                    <Col span={24}>
                                        <strong>{translate('Zaaiinstructie')}:</strong>
                                        <div style={{ marginTop: '8px' }}>{productDetails.sowing_description}</div>
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={[16, 16]}>
                                {productDetails.sowing_instruction && (
                                    <Col span={24}>
                                        <strong>{translate('Zaaiinstructie')}:</strong>
                                        <div style={{ marginTop: '8px' }}>{productDetails.sowing_instruction}</div>
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={[16, 16]}>
                                {productDetails.transplant_instructions && (
                                    <Col span={24}>
                                        <strong>{translate('Plantinstructie')}:</strong>
                                        <div style={{ marginTop: '8px' }}>{productDetails.transplant_instructions}</div>
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={[16, 16]}>
                                {productDetails.harvest_instruction && (
                                    <Col span={24}>
                                        <strong>{translate('Oogstinstructie')}:</strong>
                                        <div style={{ marginTop: '8px' }}>{productDetails.harvest_instruction}</div>
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={[16, 16]}>
                                {productDetails.locations && (
                                    <Col span={24}>
                                        <strong>{translate('Locaties')}:</strong>
                                        <div style={{ marginTop: '8px' }}>{productDetails.locations}</div>
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={[16, 16]}>
                                {productDetails.seed_type && (
                                    <Col span={12}>
                                        <strong>{translate('Zaadtype')}:</strong>
                                        <div style={{ marginTop: '8px' }}>{productDetails.seed_type}</div>
                                    </Col>
                                )}
                                {productDetails.germination_type && (
                                    <Col span={12}>
                                        <strong>{translate('Kiemtype')}:</strong>
                                        <div style={{ marginTop: '8px' }}>{productDetails.germination_type}</div>
                                    </Col>
                                )}
                            </Row>
                            <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
                                <Col span={24}>
                                    <Button 
                                        type="primary" 
                                        icon={<PlusOutlined />}
                                        onClick={addToMyCrops}
                                        loading={addingToMyCrops}
                                        size="large"
                                        style={{ width: '100%' }}
                                    >
                                        {translate('Toevoegen aan mijn gewassen')}
                                    </Button>
                                </Col>
                                {api.isAdmin() && (
                                    <Col span={24}>
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={openEditModal}
                                            size="large"
                                            style={{ width: '100%' }}
                                        >
                                            {translate('Bewerken')}
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </Card>

                        {productDetails.offers && productDetails.offers.length > 0 && (
                            <Card title={translate('Aanbieders')}>
                                <List
                                    dataSource={productDetails.offers}
                                    renderItem={offer => (
                                        <List.Item
                                            actions={[
                                                <Button 
                                                    type="primary" 
                                                    href={offer.url || offer.supplier?.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {translate('Ga naar pagina')}
                                                </Button>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    offer.supplier?.logo ? (
                                                        <Avatar src={offer.supplier.logo} size="large" />
                                                    ) : (
                                                        <Avatar size="large">{offer.supplier?.name?.[0] || '?'}</Avatar>
                                                    )
                                                }
                                                title={
                                                    <span>{offer.supplier?.name || translate('Onbekende leverancier')}</span>
                                                }
                                                description={
                                                    <div>
                                                        {offer.supplier?.website && (
                                                            <div>
                                                                <strong>{translate('Website')}:</strong>{' '}
                                                                <a href={offer.supplier.website} target="_blank" rel="noopener noreferrer">
                                                                    {offer.supplier.website}
                                                                </a>
                                                            </div>
                                                        )}
                                                        {offer.is_active ? (
                                                            <Tag color="green">{translate('Actief')}</Tag>
                                                        ) : (
                                                            <Tag color="red">{translate('Inactief')}</Tag>
                                                        )}
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        )}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        {translate('Fout bij het laden van productdetails')}
                    </div>
                )}
            </Drawer>

            <Modal
                title={translate('Product bewerken')}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleEditSave}
                confirmLoading={editLoading}
                width={800}
                okText={translate('Opslaan')}
                cancelText={translate('Annuleren')}
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item name="measurement_system" hidden>
                        <Input />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="name" label={translate('Naam')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="variety" label={translate('Ras')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="type" label={translate('Type')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="brand" label={translate('Merk')}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="sow_min" label={translate('Zaai van')}>
                                <Select options={monthOptions} allowClear />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="sow_max" label={translate('Zaai tot')}>
                                <Select options={monthOptions} allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="plant_min" label={translate('Plant van')}>
                                <Select options={monthOptions} allowClear />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="plant_max" label={translate('Plant tot')}>
                                <Select options={monthOptions} allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="harvest_min" label={translate('Oogst van')}>
                                <Select options={monthOptions} allowClear />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="harvest_max" label={translate('Oogst tot')}>
                                <Select options={monthOptions} allowClear />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="row_distance" label={translate('Rijafstand')}>
                                <InputNumber addonAfter={productDistanceUnit} style={{width: '100%'}} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="plant_distance" label={translate('Plantafstand')}>
                                <InputNumber addonAfter={productDistanceUnit} style={{width: '100%'}} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="height" label={translate('Hoogte')}>
                                <InputNumber addonAfter={productDistanceUnit} style={{width: '100%'}} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="sowing_depth" label={translate('Zaaidiepte')}>
                                <InputNumber addonAfter={productDistanceUnit} style={{width: '100%'}} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="is_direct_sown" label={translate('Direct zaaien')} valuePropName="checked">
                                <Checkbox />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="is_pricked_out" label={translate('Verspeend')} valuePropName="checked">
                                <Checkbox />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="is_seedling" label={translate('Zaailing')} valuePropName="checked">
                                <Checkbox />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="is_perennial" label={translate('Meerjarig')} valuePropName="checked">
                                <Checkbox />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="is_multiple_harvestable" label={translate('Meervoudig oogstbaar')} valuePropName="checked">
                                <Checkbox />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="germination_days" label={translate('Kiemdagen')}>
                                <Input type="number" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="prick_out_days" label={translate('Verspeendagen')}>
                                <Input type="number" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="plant_out_days" label={translate('Uitplantdagen')}>
                                <Input type="number" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="maturity_days" label={translate('Groeidagen')}>
                                <Input type="number" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="harvest_days" label={translate('Oogstdagen')}>
                                <Input type="number" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="harvest_count" label={translate('Aantal oogsten')}>
                                <Input type="number" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="buffer_days" label={translate('Bufferdagen')}>
                                <Input type="number" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="crop_type_id" label={translate('Gewastype')}>
                                <Select
                                    allowClear
                                    options={cropTypes.map(ct => ({ label: ct.name, value: ct.id }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="icon_id" label={translate('Icoon')}>
                                <Select
                                    allowClear
                                    options={icons.map(icon => ({ label: icon.name, value: icon.id }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label={translate('Beschrijving')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="sowing_instruction" label={translate('Zaaiinstructie')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="transplant_instructions" label={translate('Plantinstructie')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="harvest_instruction" label={translate('Oogstinstructie')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="location" label={translate('Locatie')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="seed_type" label={translate('Zaadtype')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="germination_type" label={translate('Kiemtype')}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="verified" label={translate('Geverifieerd')} valuePropName="checked">
                        <Input type="checkbox" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}